/* BEATSEEK — Community Ideas slide-out rail (desktop only ≥1100px).
 * Gold tab stacked ABOVE the Featured Emotes tab on the Wall's right edge.
 * Pulsing badge on the handle when there are active polls the user hasn't voted on.
 * Opt-out/opt-in footer always reachable, even with zero polls. */
import React, { useEffect, useState } from 'react';
import { Lightbulb, ChevronRight, ChevronLeft } from 'lucide-react';
import { usePolls, PollCard } from './CommunityPollsStrip';

const STORAGE_OPEN = 'beatseek_ideas_panel_open';

const pagerBtn = {
  width: '22px', height: '22px', borderRadius: '50%',
  background: 'rgba(250,204,21,0.08)', border: '1px solid rgba(250,204,21,0.35)',
  color: '#facc15', cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: 0, transition: 'all 0.15s',
};

const IdeasVotePanel = ({ userId }) => {
  const { polls, optedOut, vote, dismiss, setOptOutPref, unvotedCount } = usePolls(userId);
  const [viewport, setViewport] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const [pollIdx, setPollIdx] = useState(0);

  useEffect(() => {
    if (pollIdx > polls.length - 1) setPollIdx(Math.max(0, polls.length - 1));
  }, [polls.length, pollIdx]);
  const [open, setOpen] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(STORAGE_OPEN) === '1';
  });

  useEffect(() => {
    if (typeof window !== 'undefined') localStorage.setItem(STORAGE_OPEN, open ? '1' : '0');
  }, [open]);

  useEffect(() => {
    const onResize = () => setViewport(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  if (viewport < 1100) return null;

  const PANEL_WIDTH = 240;
  const HANDLE_WIDTH = 24;
  const left = open ? `calc(50% + 336px)` : `calc(50% + 356px)`;

  const handleOptOut = () => {
    if (!window.confirm('Hide all community vote polls? You can turn them back on anytime from this same tab.')) return;
    setOptOutPref(true);
  };

  return (
    <>
      <style>{`
        @keyframes beatseekIdeasPulse {
          0%, 100% { box-shadow: 0 8px 22px rgba(0,0,0,0.45), 0 0 0 0 rgba(250,204,21,0.0); }
          50% { box-shadow: 0 8px 22px rgba(0,0,0,0.45), 0 0 14px 4px rgba(250,204,21,0.55); }
        }
      `}</style>

      <div
        data-testid="ideas-vote-panel"
        style={{
          position: 'fixed',
          top: '5vh',
          left,
          transition: 'left 0.35s cubic-bezier(0.22, 1, 0.36, 1)',
          display: 'flex',
          alignItems: 'stretch',
        }}
      >
        {/* Panel */}
        <div
          data-testid="ideas-panel-body"
          style={{
            width: open ? `${PANEL_WIDTH}px` : '0px',
            opacity: open ? 1 : 0,
            overflow: 'hidden',
            maxHeight: open ? '28vh' : '0px',
            overflowY: 'hidden',
            background: 'linear-gradient(180deg, #0d0d14 0%, #07070b 100%)',
            borderTop: open ? '1px solid rgba(250,204,21,0.28)' : '1px solid transparent',
            borderLeft: open ? '1px solid rgba(250,204,21,0.28)' : '1px solid transparent',
            borderBottom: open ? '1px solid rgba(250,204,21,0.28)' : '1px solid transparent',
            borderRight: 'none',
            borderTopLeftRadius: '14px',
            borderBottomLeftRadius: '14px',
            padding: open ? '14px 14px 14px 28px' : '0',
            boxShadow: open ? '-10px 0 24px rgba(0,0,0,0.45), 0 0 0 1px rgba(250,204,21,0.1)' : 'none',
            transition: 'width 0.35s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.25s ease, padding 0.35s',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', justifyContent: 'flex-end' }}>
            <div style={{
              fontSize: '10px', fontWeight: 800, letterSpacing: '0.16em',
              background: 'linear-gradient(135deg, #facc15, #d97706)',
              WebkitBackgroundClip: 'text', backgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textTransform: 'uppercase',
            }}>
              BEATSEEK Idea Polls
            </div>
            <Lightbulb size={14} style={{ color: '#facc15' }} />
          </div>
          <div style={{ fontSize: '9px', color: '#64748b', marginBottom: '12px', letterSpacing: '0.06em', textAlign: 'right' }}>
            Vote on Beatseeker suggestions
          </div>

          {optedOut ? (
            <div style={{ padding: '14px', textAlign: 'center', fontSize: '11px', color: '#94a3b8',
                          border: '1px dashed rgba(250,204,21,0.2)', borderRadius: '10px' }}>
              <div style={{ marginBottom: '8px' }}>Community votes are hidden.</div>
              <button
                onClick={() => setOptOutPref(false)}
                data-testid="poll-opt-in-btn"
                style={{ background: 'rgba(250,204,21,0.1)', border: '1px solid rgba(250,204,21,0.4)',
                         color: '#facc15', fontSize: '10px', fontWeight: 700, padding: '6px 12px',
                         borderRadius: '7px', cursor: 'pointer' }}
              >
                Show community votes
              </button>
            </div>
          ) : polls.length === 0 ? (
            <div style={{ padding: '14px', textAlign: 'center', fontSize: '11px', color: '#475569',
                          border: '1px dashed rgba(255,255,255,0.08)', borderRadius: '10px' }}>
              <div style={{ marginBottom: '4px' }}>No active community votes.</div>
              <div>Drop an idea in the Suggestion Box on your Dashboard!</div>
            </div>
          ) : (
            <div>
              <PollCard poll={polls[Math.min(pollIdx, polls.length - 1)]} onVote={vote} />
              {polls.length > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginTop: '9px' }}>
                  <button
                    data-testid="ideas-poll-prev"
                    onClick={() => setPollIdx(i => (i - 1 + polls.length) % polls.length)}
                    style={pagerBtn}
                  >
                    <ChevronLeft size={13} />
                  </button>
                  <span data-testid="ideas-poll-counter" style={{ fontSize: '0.62rem', color: '#94a3b8', fontWeight: 700, letterSpacing: '0.08em' }}>
                    {Math.min(pollIdx, polls.length - 1) + 1} / {polls.length}
                  </span>
                  <button
                    data-testid="ideas-poll-next"
                    onClick={() => setPollIdx(i => (i + 1) % polls.length)}
                    style={pagerBtn}
                  >
                    <ChevronRight size={13} />
                  </button>
                </div>
              )}
            </div>
          )}

          {!optedOut && (
            <div style={{ textAlign: 'right', marginTop: '8px' }}>
              <button
                onClick={handleOptOut}
                data-testid="poll-opt-out-btn"
                style={{ background: 'none', border: 'none', color: '#565b70', fontSize: '0.62rem', cursor: 'pointer', textDecoration: 'underline' }}
              >
                Hide community votes
              </button>
            </div>
          )}
        </div>

        {/* Toggle handle */}
        <button
          data-testid="ideas-panel-toggle"
          onClick={() => setOpen(o => !o)}
          title={open ? 'Hide community ideas' : 'Show community ideas'}
          style={{
            position: 'relative',
            width: `${HANDLE_WIDTH}px`,
            background: 'linear-gradient(180deg, rgba(250,204,21,0.16), rgba(180,83,9,0.16))',
            border: '1px solid rgba(250,204,21,0.38)',
            borderTopRightRadius: '12px',
            borderBottomRightRadius: '12px',
            borderTopLeftRadius: open ? '0' : '12px',
            borderBottomLeftRadius: open ? '0' : '12px',
            color: '#facc15',
            cursor: 'pointer',
            padding: '14px 0',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
            minHeight: '110px',
            boxShadow: open ? '0 8px 22px rgba(0,0,0,0.45), 0 0 14px 3px rgba(250,204,21,0.4)' : '0 8px 22px rgba(0,0,0,0.45)',
            transition: 'transform 0.2s cubic-bezier(0.22,1,0.36,1), box-shadow 0.2s, border-color 0.2s',
            animation: (unvotedCount > 0 && !open) ? 'beatseekIdeasPulse 1.6s ease-in-out infinite' : 'none',
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'translateX(4px)'; e.currentTarget.style.boxShadow = '0 12px 28px rgba(0,0,0,0.5), 0 0 20px 5px rgba(250,204,21,0.6)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = open ? '0 8px 22px rgba(0,0,0,0.45), 0 0 14px 3px rgba(250,204,21,0.4)' : '0 8px 22px rgba(0,0,0,0.45)'; }}
        >
          {unvotedCount > 0 && !open && (
            <span
              data-testid="ideas-badge"
              style={{
                position: 'absolute', top: '-7px', right: '-7px',
                minWidth: '17px', height: '17px', borderRadius: '50%',
                background: '#facc15', color: '#1a1305',
                fontSize: '10px', fontWeight: 900,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '0 4px', boxShadow: '0 0 8px rgba(250,204,21,0.6)',
              }}
            >
              {unvotedCount}
            </span>
          )}
          {open ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
          {!open && (
            <div style={{
              writingMode: 'vertical-rl',
              transform: 'rotate(180deg)',
              fontSize: '9px', fontWeight: 800, letterSpacing: '0.18em',
              textTransform: 'uppercase',
              background: 'linear-gradient(135deg, #facc15, #d97706)',
              WebkitBackgroundClip: 'text', backgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}>
              Idea Polls
            </div>
          )}
          {!open && <Lightbulb size={12} style={{ color: '#facc15' }} />}
        </button>
      </div>
    </>
  );
};

export default IdeasVotePanel;
